export function jsonToBPMNXml(input) {
  let jsonData;
  try {
    // Step 1: Convert input to a proper JavaScript object
    if (typeof input === 'string') {
      jsonData = JSON.parse(input);
    } else if (typeof input === 'object' && input !== null) {
      jsonData = input;
    } else {
      throw new Error('Input must be either a JSON string or JavaScript object.');
    }
  } catch (e) {
    console.error('Failed to parse input:', e);
    throw new Error(`Invalid input format: ${e.message}`);
  }
  // Step 2: Validate the object structure
  if (!jsonData || typeof jsonData !== 'object' || Array.isArray(jsonData)) {
    console.error('Invalid object structure:', jsonData);
    throw new Error('Input must be a non-array object.');
  }

  // Step 3: Extract definitions with fallback
  const definitions = jsonData['bpmn:definitions'] || jsonData.definitions;
  if (!definitions) {
    console.error('Missing definitions in:', Object.keys(jsonData));
    throw new Error('Input object must contain bpmn:definitions.');
  }

  // Helper function to escape XML special characters
  const escapeXml = (unsafe) => {
    if (unsafe == null) return ''; // Handle null or undefined values
    return unsafe.toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  // Helper function to process attributes from an object
  const handleAttributes = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    return Object.entries(obj)
      .filter(([key]) => key.startsWith('@')) // Attributes are prefixed with '@'
      .map(([key, value]) => ` ${key.substring(1)}="${escapeXml(value)}"`) // Remove '@' prefix
      .join('');
  };

  // Recursive function to process elements and their children
  const processElement = (key, value, indent = 1) => {
    const spaces = '  '.repeat(indent); // Using two spaces for indentation

    // If the value is an array, process each item separately
    if (Array.isArray(value)) {
      return value.map(item => processElement(key, item, indent)).join('\n');
    }

    // If the value is not an object (e.g., text content), create a simple tag
    if (typeof value !== 'object' || value === null) {
      // Determine the element name: if it already has a known prefix or is a general BPMN element
      const elementName = key.includes(':') ? key : `bpmn:${key}`;
      return `${spaces}<${elementName}>${escapeXml(value)}</${elementName}>`;
    }

    // Handle attributes for the current element
    const attrs = handleAttributes(value);
    let children = '';

    // Process child elements recursively
    Object.entries(value).forEach(([childKey, childValue]) => {
      if (!childKey.startsWith('@')) { // Ignore attributes
        children += '\n' + processElement(childKey, childValue, indent + 1);
      }
    });

    // Determine the element name to avoid double prefixes
    let elementName;
    if (key.includes(':')) {
      // If the key already contains a prefix (e.g., "bpmndi:BPMNPlane"), use it as is
      elementName = key;
    } else {
      // Otherwise, assume it's a core BPMN element and add the "bpmn:" prefix
      elementName = `bpmn:${key}`;
    }

    // Construct the XML tag
    return children
      ? `${spaces}<${elementName}${attrs}>${children}\n${spaces}</${elementName}>`
      : `${spaces}<${elementName}${attrs} />`; // Self-closing tag if no children
  };

  // Process definitions element specifically
  const defAttrs = handleAttributes(definitions);
  let defChildren = '';

  Object.entries(definitions).forEach(([key, value]) => {
    if (!key.startsWith('@')) { // Ignore attributes of definitions itself
      defChildren += '\n' + processElement(key, value, 1);
    }
  });

  // Construct the final XML string
  return `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions${defAttrs}>${defChildren}
</bpmn:definitions>`;
}

export default jsonToBPMNXml;